import { Typography, CircularProgress } from '@mui/material';
import { Stack } from '@mui/system';
import React, { useState, useEffect, useMemo } from 'react';
import { useGetOrganizationCampaignsQuery, useRefreshCampaignDataMutation } from '../../api/campaignMonitoringSlice';
import FilterManager from './FilterManager';
import CampaignOverviewTable from './CampaignOverview/CampaignOverviewTable';
import { useContext } from 'react';
import { UserContext } from '../Hooks/useUser';
import { useLogUserActionMutation } from '../../api/apiSlice';
import MulticampaignDetails from './CampaignDetails/MulticampaignDetails';
import LastUpdatedTimestamp from '../CustomComponents/LastUpdatedTimestamp';
import { LoadingButton } from '@mui/lab';
import { theme } from '../../theme';

function CampaignMonitoring() {
    const { user } = useContext(UserContext);
    const { data: campaigns = [], isLoading, isSuccess } = useGetOrganizationCampaignsQuery(user.hospital.organization_id);
    const [selectedCampaigns, setSelectedCampaigns] = useState(new Set());
    const [appliedFilters, setAppliedFilters] = useState({});
    const [logUserAction] = useLogUserActionMutation();
    const [refreshCampaignData, { isLoading: isRefreshing }] = useRefreshCampaignDataMutation();

    const filterOptions = {
        'Location': {
            name: 'Location',
            options: [...new Set(campaigns.map((campaign) => campaign.location))],
            selectedOptions: [],
            selected: false,
        },
        'Specialties': {
            name: 'Specialties',
            options: [...new Set(campaigns.map((campaign) => campaign.specialties).flat())],
            selectedOptions: [],
            selected: false,
        },
        'Status': {
            name: 'Status',
            options: [...new Set(campaigns.map((campaign) => campaign.status))],    
            selectedOptions: ['Active'], // Default to Active status
            selected: true,
        },
    };

    const handleRefreshClick = async () => {
        try {
            await refreshCampaignData(user.hospital.organization_id).unwrap();
            logUserAction({ action: 'campaign_monitoring_refresh_clicked', data: { organization: user.hospital.organization_id } });
        } catch {
            // Error handling is managed in the slice directly
        }
    };

    // Initialize filters once campaigns are loaded
    useEffect(() => {
        if (isSuccess && campaigns.length > 0) {
            setAppliedFilters({ ...filterOptions});
        }
    }, [isSuccess, campaigns]);

    useEffect(() => {
        if (isSuccess) {
            logUserAction({ action: 'loaded_campaign_monitoring_campaigns', data: { organization: user.hospital.organization_id } });
        }
    }, [isSuccess]);

    const selectedCampaignObjects = useMemo(() => {
        return campaigns.filter(c => selectedCampaigns.has(c.id));
    }, [campaigns, selectedCampaigns]);
    
    return (
        <Stack>
            <Stack direction='row' alignItems='center' spacing={1} pb={1} px={4} sx={{ backgroundColor: theme.palette.background.white, borderBottom: '1px solid', borderColor: 'divider', top: 64, position: 'sticky', zIndex: 5 }}>
                <Typography variant='heavy' fontSize='18px'>Campaign Overview</Typography>
                { isSuccess && 
                    <FilterManager campaigns={campaigns} appliedFilters={appliedFilters} setAppliedFilters={setAppliedFilters} />
                }
                <span style={{ flexGrow: 1 }} /> 
                <LastUpdatedTimestamp timestamp={campaigns.length > 0 ? campaigns[0].last_refreshed_at : null} />
                <LoadingButton variant="outlined" size="small" onClick={handleRefreshClick} disabled={isLoading} loading={isRefreshing}> 
                    Refresh Data
                </LoadingButton>
            </Stack>
            <Stack px={4} py={4} spacing={4} sx={{ height: '100%', overflow: 'hidden' }}>
                <Stack sx={{ maxHeight: '48%'}}>
                    { isLoading && 
                        <Stack width="100%" height="100%" justifySelf="center" alignItems="center" spacing={2} pt={5}>
                            <Typography variant="h4">Loading Campaign Overview...</Typography>
                            <CircularProgress />
                        </Stack>
                    }
                    { isSuccess && 
                        <CampaignOverviewTable 
                            campaigns={campaigns} 
                            appliedFilters={appliedFilters} 
                            selectedCampaigns={selectedCampaigns} 
                            setSelectedCampaigns={setSelectedCampaigns} 
                            selectedCampaignObjects={selectedCampaignObjects}
                        />
                    }
                </Stack>
                { selectedCampaignObjects.length > 0 && 
                    <Stack sx={{ maxHeight: '48%' }}>
                        <MulticampaignDetails campaigns={selectedCampaignObjects} setSelectedCampaigns={setSelectedCampaigns} />
                    </Stack>
                }
            </Stack>
        </Stack>
    );
}

export default CampaignMonitoring;
